import { Injectable, TemplateRef, ViewContainerRef } from "@angular/core";
import { QueryList } from "@angular/core";
import { DsOptionComponent } from "../components/ds-selector-components/ds-option/ds-option.component";
import {
  Overlay,
  OverlayRef,
  FlexibleConnectedPositionStrategy,
  OverlayPositionBuilder,
  ScrollStrategy,
  ConnectedPosition,
} from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";

@Injectable({
  providedIn: "root",
})
export class DsOptionService {
  getEnabledOptions(options: QueryList<DsOptionComponent> | undefined): DsOptionComponent[] {
    return options ? Array.from(options).filter((option) => !option.disabled && !option.default) : [];
  }

  updateOptionsHighlight(options: QueryList<DsOptionComponent> | undefined, highlightedIndex: number): void {
    if (!options) return;

    const enabledOptions = this.getEnabledOptions(options);
    options.forEach((option) => {
      const enabledIndex = enabledOptions.indexOf(option);
      option.highlighted = enabledIndex === highlightedIndex;
    });
  }
  scrollToHighlightedOption(
    options: QueryList<DsOptionComponent> | undefined,
    highlightedIndex: number,
    containerElement: HTMLElement,
    optionListSelector = ".option-list-content"
  ): void {
    if (highlightedIndex < 0 || !options) return;

    const enabledOptions = this.getEnabledOptions(options);
    if (highlightedIndex >= enabledOptions.length) return;

    const highlightedOption = enabledOptions[highlightedIndex];
    if (highlightedOption && highlightedOption.elementRefInstance) {
      const optionElement = highlightedOption.elementRefInstance.nativeElement;
      const optionList = containerElement.querySelector(optionListSelector) as HTMLElement;

      if (optionList && optionElement) {
        const optionOffsetTop = optionElement.offsetTop;
        const optionHeight = optionElement.offsetHeight;
        const listHeight = optionList.clientHeight;
        const listScrollTop = optionList.scrollTop;
        const idealScrollTop = optionOffsetTop - listHeight / 2 + optionHeight / 2;
        const newScrollTop = Math.max(0, idealScrollTop);
        const isOptionVisible =
          optionOffsetTop >= listScrollTop && optionOffsetTop + optionHeight <= listScrollTop + listHeight;

        if (!isOptionVisible) {
          optionList.scrollTo({
            top: newScrollTop,
            behavior: "smooth",
          });
        }
      }
    }
  }

  findOptionByLetter(options: QueryList<DsOptionComponent> | undefined, letter: string, startIndex = 0): number {
    if (!options || !letter || letter.length !== 1) return -1;

    const enabledOptions = this.getEnabledOptions(options);
    if (enabledOptions.length === 0) return -1;

    const normalizedLetter = letter.toLowerCase();
    const searchStartIndex = startIndex % enabledOptions.length;
    for (let i = searchStartIndex; i < enabledOptions.length; i++) {
      const option = enabledOptions[i];
      if (option.displayText && option.displayText.toLowerCase().trim().charAt(0) === normalizedLetter) {
        return i;
      }
    }
    for (let i = 0; i < searchStartIndex; i++) {
      const option = enabledOptions[i];
      if (option.displayText && option.displayText.toLowerCase().trim().charAt(0) === normalizedLetter) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Finds the first enabled option whose label starts with the typed text, wrapping around
   * from startIndex so a repeated search can walk through the matches.
   */
  findOptionByText(options: QueryList<DsOptionComponent> | undefined, text: string, startIndex = 0): number {
    if (!options || !text) return -1;

    const enabledOptions = this.getEnabledOptions(options);
    if (enabledOptions.length === 0) return -1;

    const normalizedText = this.normalizeOptionText(text);
    if (!normalizedText) return -1;

    const searchStartIndex = startIndex % enabledOptions.length;
    for (let offset = 0; offset < enabledOptions.length; offset++) {
      const index = (searchStartIndex + offset) % enabledOptions.length;
      const displayText = this.normalizeOptionText(enabledOptions[index].displayText || "");
      if (displayText.startsWith(normalizedText)) {
        return index;
      }
    }

    return -1;
  }

  /**
   * Brands and models carry accents, so the comparison ignores them. Repeated spaces are
   * collapsed as well, otherwise an extra space typed by mistake would stop matching labels
   * like "Serie 3".
   */
  private normalizeOptionText(value: string): string {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  getPositionConfig(offsetY = 4): ConnectedPosition[] {
    const positionAbove = {
      originX: "start" as const,
      originY: "top" as const,
      overlayX: "start" as const,
      overlayY: "bottom" as const,
      offsetY: -offsetY,
    };
    const positionBelow = {
      originX: "start" as const,
      originY: "bottom" as const,
      overlayX: "start" as const,
      overlayY: "top" as const,
      offsetY,
    };
    return [positionBelow, positionAbove];
  }

  /**
   * Returns position config for dropdown overlay. Use 'below' or 'above' to force single direction.
   */
  getPositionConfigFor(priority: "below" | "above" | "auto", offsetY = 4): ConnectedPosition[] {
    const config = this.getPositionConfig(offsetY);
    if (priority === "below") return [config[0]];
    if (priority === "above") return [config[1]];
    return config;
  }

  /**
   * Calcula la altura disponible en el viewport según si el overlay está arriba o abajo
   */
  calculateAvailableHeight(elementRect: DOMRect, isAbove: boolean, margin = 16): number {
    const viewportHeight = window.innerHeight;
    return isAbove ? elementRect.top - margin : viewportHeight - elementRect.bottom - margin;
  }

  /**
   * Crea un ResizeObserver para actualizar el ancho del overlay cuando cambia el elemento
   */
  createResizeObserver(element: HTMLElement, overlayRef: OverlayRef, onResize?: () => void): ResizeObserver {
    const observer = new ResizeObserver(() => {
      if (overlayRef) {
        overlayRef.updateSize({ width: element.offsetWidth });
        onResize?.();
      }
    });
    observer.observe(element);
    return observer;
  }

  /**
   * Detiene y desconecta un ResizeObserver
   */
  stopResizeObserver(resizeObserver: ResizeObserver | null): null {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    return null;
  }

  /**
   * Verifica si un click fue fuera del elemento trigger y del overlay
   */
  isClickOutside(event: MouseEvent, triggerElement: HTMLElement | null, overlayRef: OverlayRef | null): boolean {
    const target = event.target as HTMLElement;
    if (triggerElement && triggerElement.contains(target)) {
      return false;
    }
    if (overlayRef) {
      const overlayElement = overlayRef.overlayElement;
      if (overlayElement && overlayElement.contains(target)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Crea un FlexibleConnectedPositionStrategy configurado
   */
  createPositionStrategy(
    overlayPositionBuilder: OverlayPositionBuilder,
    element: HTMLElement,
    options?: {
      positions?: ConnectedPosition[];
      viewportMargin?: number;
      flexibleDimensions?: boolean;
      growAfterOpen?: boolean;
      push?: boolean;
    }
  ): FlexibleConnectedPositionStrategy {
    const defaultOptions = {
      positions: this.getPositionConfig(),
      viewportMargin: 16,
      flexibleDimensions: true,
      growAfterOpen: true,
      push: false,
    };
    const config = { ...defaultOptions, ...options };

    return overlayPositionBuilder
      .flexibleConnectedTo(element)
      .withPositions(config.positions)
      .withPush(config.push)
      .withFlexibleDimensions(config.flexibleDimensions)
      .withViewportMargin(config.viewportMargin)
      .withGrowAfterOpen(config.growAfterOpen);
  }

  /**
   * Crea y configura un OverlayRef
   */
  createOverlay(
    overlay: Overlay,
    positionStrategy: FlexibleConnectedPositionStrategy,
    element: HTMLElement,
    options?: {
      hasBackdrop?: boolean;
      backdropClass?: string;
      scrollStrategy?: ScrollStrategy;
    }
  ): OverlayRef {
    const defaultOptions = {
      hasBackdrop: true,
      backdropClass: "cdk-overlay-transparent-backdrop",
    };
    const config = { ...defaultOptions, ...options };

    return overlay.create({
      positionStrategy,
      scrollStrategy: config.scrollStrategy ?? overlay.scrollStrategies.reposition({ scrollThrottle: 0 }),
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      width: element.offsetWidth,
    });
  }

  /**
   * Adjunta un template al overlay y retorna el portal
   */
  attachTemplate(overlayRef: OverlayRef, template: TemplateRef<any>, vcr: ViewContainerRef): TemplatePortal {
    const portal = new TemplatePortal(template, vcr);
    overlayRef.attach(portal);
    return portal;
  }

  /**
   * Configura el listener para cambios de posición y ajusta maxHeight
   */
  setupPositionChangeListener(
    positionStrategy: FlexibleConnectedPositionStrategy,
    element: HTMLElement,
    overlayRef: OverlayRef,
    margin = 16
  ): void {
    positionStrategy.positionChanges.subscribe((change) => {
      if (overlayRef) {
        const rect = element.getBoundingClientRect();
        const isAbove = change.connectionPair.originY === "top";
        const availableHeight = this.calculateAvailableHeight(rect, isAbove, margin);
        overlayRef.updateSize({ maxHeight: `${availableHeight}px` });
      }
    });
  }

  /**
   * Cierra y destruye un overlay con animación
   */
  disposeOverlay(overlayRef: OverlayRef | null, delay = 300): Promise<void> {
    return new Promise((resolve) => {
      if (!overlayRef) {
        resolve();
        return;
      }
      setTimeout(() => {
        overlayRef.dispose();
        resolve();
      }, delay);
    });
  }
}
